<script setup lang="ts">
import { LoaderCircle, Paperclip, Send } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import MessageWorkspaceSurface from './MessageWorkspaceSurface.vue';
import { canSendMessageDraft, MESSAGE_ATTACHMENT_ACCEPT } from '~/lib/messageAttachments';

const props = withDefaults(defineProps<{
    modelValue?: string;
    disabled?: boolean;
    sending?: boolean;
    placeholder?: string;
    maxHeight?: number;
    hasAttachments?: boolean;
    attachmentsDisabled?: boolean;
}>(), {
    modelValue: '',
    disabled: false,
    sending: false,
    placeholder: 'Type a message...',
    maxHeight: 160,
    hasAttachments: false,
    attachmentsDisabled: false,
});

const emit = defineEmits<{
    'update:modelValue': [value: string];
    send: [];
    filesSelected: [files: File[]];
}>();

const textareaRef = ref<{ $el: HTMLTextAreaElement } | null>(null);
const fileInput = ref<HTMLInputElement | null>(null);

const value = computed({
    get: () => props.modelValue,
    set: (nextValue: string | number) => emit('update:modelValue', String(nextValue)),
});

function resizeTextarea() {
    const textarea = textareaRef.value?.$el;
    if (!textarea) return;

    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, props.maxHeight)}px`;
    textarea.style.overflowY = textarea.scrollHeight > props.maxHeight ? 'auto' : 'hidden';
}

function sendMessage() {
    if (props.disabled || props.sending || !canSendMessageDraft(props.modelValue, props.hasAttachments ? 1 : 0)) return;
    emit('send');
}

function handleFiles(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    if (files.length) emit('filesSelected', files);
    input.value = '';
}

function handleKeydown(event: KeyboardEvent) {
    if (event.key !== 'Enter' || event.shiftKey || event.isComposing) return;
    event.preventDefault();
    sendMessage();
}

watch(() => props.modelValue, async () => {
    await nextTick();
    resizeTextarea();
});

onMounted(resizeTextarea);
</script>

<template>
    <MessageWorkspaceSurface>
        <slot name="attachments" />
        <Textarea
            ref="textareaRef"
            v-model="value"
            :placeholder="placeholder"
            :disabled="disabled || sending"
            aria-label="Message"
            class="!min-h-8 resize-none overflow-y-hidden !border-0 bg-transparent px-0 pb-0 pt-2 !shadow-none focus-visible:!border-transparent focus-visible:!ring-0 dark:bg-transparent"
            rows="1"
            @input="resizeTextarea"
            @keydown="handleKeydown"
        />

        <div class="flex min-h-6 items-end justify-between gap-3">
            <div class="flex min-w-0 flex-1 items-center gap-2">
                <input ref="fileInput" type="file" multiple class="hidden" :accept="MESSAGE_ATTACHMENT_ACCEPT" :disabled="attachmentsDisabled || disabled || sending" @change="handleFiles">
                <Tooltip>
                    <TooltipTrigger as-child>
                        <Button type="button" variant="ghost" size="icon" class="shrink-0 rounded-full" :disabled="attachmentsDisabled || disabled || sending" aria-label="Attach files" @click="fileInput?.click()">
                            <Paperclip class="size-4" />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top">Attach files</TooltipContent>
                </Tooltip>
                <slot />
            </div>
            <Tooltip>
                <TooltipTrigger as-child>
                    <Button
                        type="button"
                        size="icon"
                        class="size-9 shrink-0 rounded-full"
                        :disabled="disabled || sending || !canSendMessageDraft(modelValue, hasAttachments ? 1 : 0)"
                        aria-label="Send message"
                        @click="sendMessage"
                    >
                        <LoaderCircle v-if="sending" class="size-4 animate-spin" />
                        <Send v-else class="size-4" />
                    </Button>
                </TooltipTrigger>
                <TooltipContent side="top">Send message</TooltipContent>
            </Tooltip>
        </div>
    </MessageWorkspaceSurface>
</template>
